// Leads kit: the design helpers from components/crm/ui.tsx, components/ui.tsx and
// components/TimeInput.tsx that crm/ui.js does not have yet, plus the mapping of
// backend lead rows to the design's vocabulary (state/crm.tsx). No sample data.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Clock,
  Columns3,
  Eye,
  Facebook,
  Footprints,
  Globe,
  GripVertical,
  Instagram,
  Lock,
  MapPin,
  Megaphone,
  MessageCircle,
  PenLine,
  Pencil,
  Phone,
  RotateCcw,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import config from "../../../config";
import { SearchSelect } from "../SearchSelect";
import { DAY, NO_DATA } from "../../lib/format";
import { SearchBox, StatusBadge, leadStatusLabel, paper, statusDot, useOutside } from "./ui";

export { glass } from "../wallet/parts";

/* =====================================================================
   API
   ===================================================================== */

export const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
/** GET; resolves to the JSON body, or null when the call fails. */
export const apiGet = (path, params) => axios.get(`${config.API_BASE_URL}${path}`, { ...authHeaders(), params }).then((r) => r.data).catch(() => null);
/** Writes reject on failure so callers never show a success that didn't happen. */
export const apiPost = (path, body) => axios.post(`${config.API_BASE_URL}${path}`, body, authHeaders()).then((r) => r.data);
export const apiPut = (path, body) => axios.put(`${config.API_BASE_URL}${path}`, body, authHeaders()).then((r) => r.data);
export const apiDelete = (path) => axios.delete(`${config.API_BASE_URL}${path}`, authHeaders()).then((r) => r.data);
export const apiError = (e, fallback) => e?.response?.data?.message || fallback;

/* =====================================================================
   Vocabulary (design: state/crm.tsx) mapped to the backend's values
   ===================================================================== */

/** Backend `leads.source` → the design's source names. */
export const SOURCE_LABEL = {
  meta_leads: "Meta lead ads",
  meta_facebook: "Facebook",
  meta_instagram: "Instagram",
  website_form: "Website form",
  google_business: "Google Business",
  google_ads: "Google Ads",
  whatsapp: "WhatsApp",
  walk_in: "Walk-in",
  referral: "Referral",
  manual: "Manual entry",
  phone_call: "Phone call",
  other: "Other",
};
export const SOURCES = Object.values(SOURCE_LABEL);
export const sourceLabel = (key) => SOURCE_LABEL[key] || (key ? String(key).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Manual entry");
export const sourceKey = (label) => Object.keys(SOURCE_LABEL).find((k) => SOURCE_LABEL[k] === label) || "";

export const PRIORITIES = ["High", "Medium", "Low"];
export const priorityLabel = (p) => (p === "high" || p === "urgent" ? "High" : p === "low" ? "Low" : "Medium");
export const priorityKey = (label) => String(label || "Medium").toLowerCase();

// Fixed choice lists from the design (vocabulary, not sample records).
export const TITLES = ["Mr.", "Ms.", "Mrs.", "Dr.", "Master", "Baby"];
export const BUDGETS = ["Under ₹5,000", "₹5,000 to ₹15,000", "₹15,000 to ₹50,000", "Above ₹50,000"];
export const CONTACT_TYPES = ["Individual", "Business"];

/** Lead tasks: backend type/status ↔ the design's. */
export const TASK_STATUSES = ["To do", "In progress", "Done", "Cancelled"];
const TASK_STATUS_LABEL = { pending: "To do", overdue: "To do", in_progress: "In progress", completed: "Done", cancelled: "Cancelled" };
const TASK_STATUS_KEY = { "To do": "pending", "In progress": "in_progress", Done: "completed", Cancelled: "cancelled" };
export const taskStatusLabel = (s) => TASK_STATUS_LABEL[s] || "To do";
export const taskStatusKey = (label) => TASK_STATUS_KEY[label] || "pending";
const TASK_TYPE_LABEL = { follow_up_call: "Call", callback: "Call", follow_up_email: "Email", meeting: "Meeting", demo: "Meeting", site_visit: "Meeting" };
export const taskTypeLabel = (t) => TASK_TYPE_LABEL[t] || "Follow-up";

/**
 * The clinic's lead statuses (GET /api/lead-statuses) as the design's names.
 * Closed = maps to lost or junk (the design's Not interested / Lost / Junk).
 */
export function statusOptions(rows) {
  return (rows || []).map((s) => ({
    key: s.key_name,
    name: leadStatusLabel(s.key_name, s.label),
    system: s.maps_to_system || s.key_name,
    closed: ["lost", "junk"].includes(s.maps_to_system || s.key_name),
    won: (s.maps_to_system || s.key_name) === "won",
    isDefault: !!s.is_default,
  }));
}
export const statusName = (options, key) => options.find((o) => o.key === key)?.name || leadStatusLabel(key);
export const statusKeyOf = (options, name) => options.find((o) => o.name === name)?.key || "";

/* =====================================================================
   Formats (design: state/crm.tsx) — against the real clock
   ===================================================================== */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fullName = (c) => `${c?.firstName || ""} ${c?.lastName || ""}`.trim();
export const initialsOf = (c) => `${(c?.firstName || "")[0] || ""}${(c?.lastName || "")[0] || ""}`.toUpperCase();
export const fmtPhone = (p) => {
  if (!p) return "";
  let d = String(p).replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : String(p);
};
export const phone10 = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  return d.length === 12 && d.startsWith("91") ? d.slice(2) : d.slice(-10);
};
export const fmtShortDate = (ts) => {
  const d = new Date(ts);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
export const fmtTime = (ts) => {
  const d = new Date(ts);
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
export const fmtDateTime = (ts) => `${fmtShortDate(ts)}, ${fmtTime(ts)}`;
/** When a lead came in: ["3 hours ago", ""] for the first 24 hours, then ["22 Oct 2026", "4:30 PM"] (date, with the time on the line below). */
export function fmtCreated(ts, now = Date.now()) {
  const min = Math.floor((now - ts) / 60_000);
  if (min < 1) return ["Just now", ""];
  if (min < 60) return [`${min} min ago`, ""];
  const h = Math.floor(min / 60);
  if (h < 24) return [`${h} ${h === 1 ? "hour" : "hours"} ago`, ""];
  return [fmtShortDate(ts), fmtTime(ts)];
}
const todayStart = () => new Date().setHours(0, 0, 0, 0);
/** Task due (date + time): "Today, 4:00 PM", "Overdue by 2 days" (design: fmtDue). */
export function fmtDue(ts) {
  const now = Date.now();
  const d = Math.floor((ts - todayStart()) / DAY);
  if (ts < now && d < 0) return d === -1 ? "Overdue since yesterday" : `Overdue by ${-d} days`;
  if (d === 0) return ts < now ? `Overdue, today ${fmtTime(ts)}` : `Today, ${fmtTime(ts)}`;
  if (d === 1) return `Tomorrow, ${fmtTime(ts)}`;
  return fmtDateTime(ts);
}
/** Lead follow-up is a date only (leads.next_follow_up_date), so no time is shown. */
export function fmtFollowUp(dayMs) {
  const d = Math.round((dayMs - todayStart()) / DAY);
  if (d < 0) return d === -1 ? "Overdue since yesterday" : `Overdue by ${-d} days`;
  if (d === 0) return "Today";
  if (d === 1) return "Tomorrow";
  return fmtShortDate(dayMs);
}
export const isoDay = (ts) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const fromIso = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
};
export const toMs = (v) => (v ? new Date(v).getTime() : null);
/** A DATE column (serialised as an ISO timestamp) → local midnight of that day. */
export const dayMs = (v) => (v ? new Date(v).setHours(0, 0, 0, 0) : null);

/* =====================================================================
   Backend lead row → the design's lead
   ===================================================================== */

export function toLead(row, statuses = [], doctors = []) {
  const statusKey = row.status_key || row.status || "new";
  const tags = (row.tags || []).map((t) => (typeof t === "string" ? t : t.name)).filter(Boolean);
  const doctor = row.preferred_doctor_name || doctors.find((d) => String(d.id) === String(row.preferred_doctor_id))?.name || "";
  return {
    id: row.id,
    ref: `#${row.id}`,
    contactId: row.contact_id || null,
    firstName: row.first_name || "",
    lastName: row.last_name || "",
    phone: row.phone || "",
    altPhone: row.alternate_phone || "",
    email: row.email || "",
    city: row.contact_city ?? null,
    statusKey,
    status: statusName(statuses, statusKey),
    closed: (statuses.find((s) => s.key === statusKey)?.closed) ?? ["lost", "junk"].includes(row.status),
    sourceKey: row.source || "manual",
    source: sourceLabel(row.source),
    priority: priorityLabel(row.priority),
    score: Number(row.lead_score) || 0,
    service: row.interested_service || "",
    doctorId: row.preferred_doctor_id || null,
    doctor,
    facilityId: row.facility_id || null,
    facility: row.facility_name || "",
    budget: row.budget_range || "",
    tags,
    tagIds: (row.tags || []).map((t) => (typeof t === "object" ? t.id : null)).filter(Boolean),
    assigneeId: row.assigned_to || null,
    assignee: row.assigned_to_name || "",
    nextFollowUp: dayMs(row.next_follow_up_date),
    notes: row.notes || "",
    lostReason: row.lost_reason || "",
    createdAt: toMs(row.created_at) || 0,
    updatedAt: toMs(row.updated_at) || 0,
    utm: { source: row.utm_source || "", medium: row.utm_medium || "", campaign: row.utm_campaign || "", content: row.utm_content || "", term: row.utm_term || "" },
    adName: row.ad_name || "",
    formName: row.meta_form_name || "",
    raw: row,
  };
}

/* =====================================================================
   Surfaces and badges (design: components/crm/ui.tsx)
   ===================================================================== */

/** Morning light behind glass cards, so glass has colour to frost. Put inside a `relative isolate` parent. */
export function MorningLight({ className = "-inset-x-10 -top-10 h-[360px]" }) {
  return (
    <div aria-hidden className={`pointer-events-none absolute -z-10 ${className}`}>
      <span className="absolute right-[4%] top-[4%] h-[300px] w-[460px] animate-glow-in rounded-full bg-sky/55 blur-3xl" />
      <span className="absolute left-[2%] top-[20%] h-[240px] w-[380px] animate-glow-in rounded-full bg-sun/45 blur-3xl" style={{ animationDelay: "150ms" }} />
    </div>
  );
}

const TASK_STYLE = {
  "To do": "bg-mist text-ink",
  "In progress": "bg-sky-soft text-ink",
  Done: "bg-success-soft text-success",
  Cancelled: "bg-mist text-muted line-through",
};
export function TaskStatusBadge({ status }) {
  return <span className={`inline-flex h-6 items-center whitespace-nowrap rounded-full px-2.5 text-caption font-semibold ${TASK_STYLE[status] || TASK_STYLE["To do"]}`}>{status}</span>;
}

export { PriorityBadge } from "./ui";

const SOURCE_ICON = {
  "Meta lead ads": Megaphone,
  Facebook,
  Instagram,
  "Website form": Globe,
  "Google Business": MapPin,
  "Google Ads": Megaphone,
  WhatsApp: MessageCircle,
  "Walk-in": Footprints,
  Referral: UserPlus,
  "Manual entry": PenLine,
  "Phone call": Phone,
};
export function SourceIcon({ source, size = 14 }) {
  const Icon = SOURCE_ICON[source] || PenLine;
  return <Icon size={size} strokeWidth={1.75} />;
}
// The apps' own colours: Instagram's gradient, Facebook and Meta blue, WhatsApp green.
const SOURCE_BRAND = {
  Instagram: { background: "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285aeb 90%)" },
  Facebook: { background: "#1877f2" },
  "Meta lead ads": { background: "#0866ff" },
  WhatsApp: { background: "#25d366" },
};
export function SourceLabel({ source }) {
  const brand = SOURCE_BRAND[source];
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-small">
      <span style={brand} className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${brand ? "text-white" : "bg-mist text-ink"}`}>
        <SourceIcon source={source} size={13} />
      </span>
      {source}
    </span>
  );
}

/** Lead score as a small meter. Number always shown. */
export function ScoreMeter({ score }) {
  return (
    <span className="inline-flex items-center gap-2 text-small tabular-nums" title={`Lead score ${score} of 100`}>
      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-mist">
        <span className={`block h-full origin-left animate-grow-x rounded-full ${score >= 70 ? "bg-ink" : "bg-muted/60"}`} style={{ width: `${Math.min(100, score)}%` }} />
      </span>
      {score}
    </span>
  );
}

export const inputCls =
  "h-11 w-full rounded-sm border border-line bg-paper px-3.5 text-small text-ink outline-none placeholder:text-muted/70 hover:border-muted/50 focus:border-ink focus:ring-2 focus:ring-ink/10";

/** Collapsible form section. */
export function FormSection({ icon, title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`${paper} overflow-hidden`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="flex h-14 w-full items-center gap-3 px-6 text-left hover:bg-mist/50">
        <span className="text-ink">{icon}</span>
        <span className="flex-1 text-body font-semibold">{title}</span>
        <ChevronDown size={18} strokeWidth={1.75} className={`text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-250 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="grid grid-cols-2 gap-x-5 gap-y-4 border-t border-line px-6 pb-6 pt-5">{children}</div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Columns ---------- */

export function useColumns(all, stored, save) {
  const defaults = useMemo(() => all.filter((c) => c.default || c.locked).map((c) => c.id), [all]);
  const ids = stored ?? defaults;
  const visible = ids.map((id) => all.find((c) => c.id === id)).filter(Boolean);
  return {
    visible,
    ids,
    toggle: (id) => save(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]),
    move: (id, dir) => {
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      const next = [...ids];
      [next[i], next[j]] = [next[j], next[i]];
      save(next);
    },
    place: (id, to) => {
      const next = ids.filter((x) => x !== id);
      next.splice(Math.max(0, Math.min(to, next.length)), 0, id);
      if (next.join() !== ids.join()) save(next);
    },
    reset: () => save(defaults),
    isDefault: ids.join() === defaults.join(),
  };
}

/** "Columns" button: tick to show, drag to reorder, reset to default. */
export function ColumnPicker({ all, cols, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  const [dragId, setDragId] = useState(null);
  const ref = useOutside(() => setOpen(false), open);
  const shown = cols.ids.map((id) => all.find((c) => c.id === id)).filter(Boolean);
  const hidden = all.filter((c) => !cols.ids.includes(c.id) && c.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <button aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="flex h-10 items-center gap-2 rounded-full bg-paper px-4 text-small font-medium ring-1 ring-line hover:ring-muted/50">
        <Columns3 size={16} strokeWidth={1.75} />
        Columns
        <span className="tabular-nums text-muted">{cols.ids.length}</span>
      </button>
      {open && (
        <div role="dialog" aria-label="Table columns" className="absolute right-0 top-12 z-30 flex max-h-[520px] w-[380px] animate-nav-in flex-col rounded-md bg-paper shadow-md ring-1 ring-line">
          <div className="flex items-center justify-between px-4 pb-2 pt-3.5">
            <span className="text-small font-semibold">Shown in table</span>
            {!cols.isDefault && (
              <button onClick={cols.reset} className="flex items-center gap-1 text-caption font-semibold text-sky-deep hover:underline">
                <RotateCcw size={12} strokeWidth={2} />
                Reset
              </button>
            )}
          </div>
          <ul className="flex flex-wrap gap-1.5 px-4 pb-3 pt-1" aria-label="Shown columns, drag to reorder">
            {shown.map((c, i) => (
              <li
                key={c.id}
                draggable
                tabIndex={0}
                aria-label={`${c.label}, position ${i + 1} of ${shown.length}. Use left and right arrow keys to move`}
                onDragStart={(e) => {
                  setDragId(c.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragId && dragId !== c.id) cols.place(dragId, i);
                }}
                onDragEnd={() => setDragId(null)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" && i > 0) cols.move(c.id, -1);
                  if (e.key === "ArrowRight" && i < shown.length - 1) cols.move(c.id, 1);
                  if ((e.key === "Delete" || e.key === "Backspace") && !c.locked) cols.toggle(c.id);
                }}
                className={`flex h-8 cursor-grab items-center gap-1 rounded-full pl-2 text-caption font-medium outline-none ring-1 focus-visible:ring-2 focus-visible:ring-ink/30 active:cursor-grabbing ${c.locked ? "bg-mist pr-3 ring-transparent" : "bg-paper pr-1 ring-line hover:ring-muted/50"} ${dragId === c.id ? "opacity-40" : ""}`}
              >
                <GripVertical size={12} strokeWidth={1.75} className="text-muted/60" />
                {c.label}
                {c.locked ? (
                  <Lock size={11} strokeWidth={2} className="ml-0.5 text-muted" aria-label="Always shown" />
                ) : (
                  <button type="button" tabIndex={-1} aria-label={`Remove ${c.label}`} title="Remove" onClick={() => cols.toggle(c.id)} className="flex h-6 w-6 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-danger">
                    <X size={12} strokeWidth={2} />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <p className="-mt-1 px-4 pb-1 text-caption text-muted">Drag to reorder</p>
          <div className="mt-2 flex flex-col gap-2 border-t border-line px-4 pb-2 pt-3">
            <span className="text-small font-semibold">Add a column</span>
            <SearchBox value={q} onChange={setQ} placeholder="Find a field" className="w-full" />
          </div>
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto px-1.5 pb-2">
            {hidden.length === 0 && <li className="px-3 py-3 text-caption text-muted">{q ? `No field called "${q}"` : "All fields are shown"}</li>}
            {hidden.map((c) => (
              <li key={c.id}>
                <button onClick={() => cols.toggle(c.id)} className="flex h-9 w-full items-center justify-between rounded-xs px-3 text-left text-small hover:bg-mist">
                  {c.label}
                  <span className="text-caption font-semibold text-sky-deep">Add</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------- Detail page parts ---------- */

/** Label over value. Empty values show a quiet "Not added" so the grid stays even. */
export function KV({ label, children, mono }) {
  const empty = children === "" || children === null || children === undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-caption text-muted">{label}</dt>
      <dd className={`min-w-0 break-words text-small ${mono ? "font-mono text-[13px]" : ""} ${empty ? "text-muted/60" : "text-ink"}`}>{empty ? "Not added" : children}</dd>
    </div>
  );
}

export function SectionCard({ title, action, children, className = "" }) {
  return (
    <section aria-label={title} className={`${paper} flex flex-col gap-5 p-6 ${className}`}>
      <div className="flex min-h-9 items-center justify-between gap-4">
        <h2 className="text-h3 font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** View, edit, delete as quiet icon buttons. Same order in every table. Leave out onEdit / onDelete to hide them (view only). */
export function RowActions({ label, onView, onEdit, onDelete }) {
  const btn = "flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors duration-150 hover:bg-paper hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20";
  return (
    <span className="flex items-center gap-0.5">
      <button type="button" onClick={onView} aria-label={`View ${label}`} title="View" className={btn}>
        <Eye size={16} strokeWidth={1.75} />
      </button>
      {onEdit && (
        <button type="button" onClick={onEdit} aria-label={`Edit ${label}`} title="Edit" className={btn}>
          <Pencil size={16} strokeWidth={1.75} />
        </button>
      )}
      {onDelete && (
        <button type="button" onClick={onDelete} aria-label={`Delete ${label}`} title="Delete" className={`${btn} hover:text-danger`}>
          <Trash2 size={16} strokeWidth={1.75} />
        </button>
      )}
    </span>
  );
}

/**
 * Status badge that opens a status menu (portalled, fixed). Closed outcomes sit
 * below a hairline. `options` are the clinic's statuses from statusOptions();
 * `status` and `onChange` use the design's status names.
 */
export function StatusPicker({ status, onChange, label, options = [] }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const menu = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const h = 360;
    const below = window.innerHeight - r.bottom > h;
    setPos({ left: Math.floor(Math.min(r.left, window.innerWidth - 232)), top: Math.floor(below ? r.bottom + 6 : Math.max(8, r.top - h - 6)) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "mousedown" && (menu.current?.contains(e.target) || btn.current?.contains(e.target))) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const groups = [options.filter((o) => !o.closed).map((o) => o.name), options.filter((o) => o.closed).map((o) => o.name)].filter((g) => g.length);
  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status of ${label}: ${status}. Change status`}
        onClick={() => setOpen((o) => !o)}
        className="group/status -ml-1 flex items-center gap-0.5 rounded-full p-0.5 outline-none hover:ring-1 hover:ring-line focus-visible:ring-2 focus-visible:ring-ink/20"
      >
        <StatusBadge status={status} />
        <ChevronDown size={14} strokeWidth={1.75} className="mr-1 text-muted opacity-0 transition-opacity group-hover/status:opacity-100 group-aria-expanded/status:opacity-100" />
      </button>
      {open &&
        pos &&
        createPortal(
          <ul ref={menu} role="listbox" aria-label="Lead status" style={{ top: pos.top, left: pos.left }} className="olli fixed z-50 w-56 animate-nav-in rounded-md bg-paper p-1.5 font-sans text-ink shadow-md ring-1 ring-line">
            {groups.map((g, gi) => (
              <li key={gi} className={gi ? "mt-1.5 border-t border-line pt-1.5" : ""}>
                <ul>
                  {g.map((s) => (
                    <li key={s}>
                      <button
                        role="option"
                        aria-selected={s === status}
                        onClick={() => {
                          setOpen(false);
                          if (s !== status) onChange(s);
                        }}
                        className={`flex h-9 w-full items-center gap-2.5 rounded-xs px-3 text-left text-small ${s === status ? "bg-mist font-semibold" : "hover:bg-mist"}`}
                      >
                        <span className={`h-2 w-2 shrink-0 rounded-full ${statusDot(s)}`} />
                        <span className="flex-1">{s}</span>
                        {s === status && <Check size={14} strokeWidth={2} />}
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </>
  );
}

/* =====================================================================
   Form controls (design: components/ui.tsx, components/TimeInput.tsx)
   ===================================================================== */

export function Field({ id, label, optional, hint, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-small font-medium text-ink">
        {label}
        {optional && <span className="font-normal text-muted"> (optional)</span>}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="flex items-center gap-1.5 text-caption font-medium text-danger">
          <AlertCircle size={14} strokeWidth={1.75} className="shrink-0" />
          {error}
        </p>
      ) : (
        hint && <p className="text-caption text-muted">{hint}</p>
      )}
    </div>
  );
}

const inputBase = "h-12 w-full rounded-sm border bg-paper px-4 text-body text-ink outline-none placeholder:text-muted/70 focus:border-ink focus:ring-2 focus:ring-ink/10";

export function TextInput({ invalid, className = "", ...props }) {
  return (
    <input
      {...props}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${props.id}-error` : undefined}
      className={`${inputBase} ${invalid ? "border-danger" : "border-line hover:border-muted/50"} ${className}`}
    />
  );
}

export function PrefixInput({ prefix, invalid, ...props }) {
  return (
    <div className={`flex h-12 items-center rounded-sm border bg-paper focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/10 ${invalid ? "border-danger" : "border-line hover:border-muted/50"}`}>
      <span className="flex h-full items-center border-r border-line px-4 text-body text-muted">{prefix}</span>
      <input
        {...props}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${props.id}-error` : undefined}
        className="h-full w-full rounded-r-sm bg-transparent px-4 text-body text-ink outline-none placeholder:text-muted/70"
      />
    </div>
  );
}

export function SelectInput({ id, disabled, options, placeholder, invalid, onChange, value }) {
  return (
    <SearchSelect
      id={id}
      value={String(value ?? "")}
      options={options}
      placeholder={placeholder}
      onChange={(next) => onChange?.({ target: { value: next }, currentTarget: { value: next } })}
      disabled={disabled}
      invalid={invalid}
      searchable
      className="w-full"
    />
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button {...props} className={`flex h-12 items-center justify-center gap-2 rounded-full px-5 text-body font-semibold text-ink hover:bg-mist ${className}`}>
      {children}
    </button>
  );
}

export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

/** "14:30" → "2:30 PM". */
export function to12h(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const suffix = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
}

/** 12-hour time picker; value stays "HH:MM" (24h). */
export function TimeInput({ id, value, onChange, step = 15, min = "00:00", max = "23:59", invalid, disabled, size = "md", placeholder = "Choose time", ariaLabel, className = "" }) {
  const options = useMemo(() => {
    const toMin = (t) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const list = [];
    for (let t = toMin(min); t <= toMin(max); t += step) list.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
    if (value && !list.includes(value)) list.push(value);
    list.sort();
    // The small size drops the clock icon: in a narrow row (a shift on a phone) it pushed "9:00 AM" to "9:0…".
    return list.map((t) => ({ value: t, label: to12h(t), ...(size === "sm" ? {} : { leading: <Clock size={14} strokeWidth={1.75} className="shrink-0 text-muted" /> }) }));
  }, [step, min, max, value, size]);
  return (
    <SearchSelect
      id={id}
      value={value}
      options={options}
      onChange={onChange}
      searchable
      searchPlaceholder="Type a time, like 7:30 PM"
      placeholder={placeholder}
      invalid={invalid}
      disabled={disabled}
      size={size}
      ariaLabel={ariaLabel}
      className={className}
      emptyText="No time matches. Try 9:00 AM."
    />
  );
}

export { NO_DATA };
